'use es6';

import { List, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { PureComponent } from 'react';
import styled from 'styled-components';
import { getType } from '../../../common-message-format/operators/commonMessageFormatGetters';
import ThemeConfigContext from '../../../web/primary-message/provider/ThemeConfigContext';
import SenderMap from '../../../proptypes/SenderMap';
import { LegacyRecipientMapPropType } from '../../../proptypes/RecipientMap';
import AsyncFileAttachmentsMap from '../../../proptypes/AsyncFileAttachmentsMap';
import { TYPICAL_RESPONSE_TIME } from '../../../typical-response-time/constants/messageTypes';
import { areMessagesGrouped } from '../../../message-history/operators/areMessagesGrouped';
import { getAttachmentsForMessage } from '../../../message-history/operators/getAttachmentsForMessage';
import { getMessageId } from '../../../message-history/operators/getMessageId';
import { getPositionDescriptors } from '../../../message-history/operators/getPositionDescriptors';
import MessageHistoryItem from './MessageHistoryItem';

// https://git.hubteam.com/HubSpot/conversations-team/issues/4679
import { jsx as _jsx } from "react/jsx-runtime";
const ReflowElement = styled.div.withConfig({
  displayName: "MessageHistory__ReflowElement",
  componentId: "pszmwd-0"
})(["z-index:0;padding-top:4px;padding-left:16px;padding-right:16px;flex-shrink:0;@media screen and (max-width:900px){padding-left:8px;padding-right:8px;}"]);
class MessageHistory extends PureComponent {
  getDontIncludeLastInGroup(message, index) {
    const {
      messages
    } = this.props;
    const nextNextMessage = messages.get(index + 2);
    if (nextNextMessage && areMessagesGrouped(message, nextNextMessage)) {
      return true;
    }
    return false;
  }
  renderHistory() {
    const {
      allowContactPromotions,
      fileAttachments,
      friendlyNames,
      getAdditionalPositionDescriptors,
      historyConfig,
      indexedAsyncFileAttachments,
      lastMessageId,
      messages,
      portalId,
      presentSenderLocators,
      senders,
      ungatedForNextBestActions,
      ungatedForReplyToAllMessages,
      visitorLastReadTimestamp,
      vid,
      recipientVisitors,
      threadHistory,
      threadId,
      legacyRecipients,
      channelName,
      slots
    } = this.props;
    const additionalPositionDescriptors = getAdditionalPositionDescriptors(messages);
    return messages.map((message, index) => {
      const previousMessage = index > 0 ? messages.get(index - 1) : undefined;
      const nextMessage = messages.get(index + 1);
      let dontIncludeLastInGroup;

      /*this logic location is not ideal. See https://git.hubteam.com/HubSpot/conversations-message-history/pull/156
      before adding any additional funky logic to this file*/
      //don't show same time on message directly before and after response time message
      if (getType(nextMessage) === TYPICAL_RESPONSE_TIME) {
        dontIncludeLastInGroup = this.getDontIncludeLastInGroup(message, index);
      }
      const fileAttachmentsForMessage = getAttachmentsForMessage(message, fileAttachments);
      let positionDescriptors = getPositionDescriptors(message, previousMessage, nextMessage, dontIncludeLastInGroup);
      const messagePojo = message.toJS();
      const {
        hiddenBy,
        id: messageId
      } = messagePojo;
      positionDescriptors = positionDescriptors.merge(additionalPositionDescriptors.get(messageId, ImmutableSet()));
      return /*#__PURE__*/_jsx(MessageHistoryItem, {
        allowContactPromotions: allowContactPromotions,
        hiddenBy: hiddenBy,
        historyConfig: historyConfig,
        fileAttachments: fileAttachmentsForMessage,
        fileAttachmentsForThread: fileAttachments,
        friendlyNames: friendlyNames,
        indexedAsyncFileAttachments: indexedAsyncFileAttachments,
        portalId: portalId,
        positionDescriptors: positionDescriptors,
        message: message,
        senders: senders,
        presentSenderLocators: presentSenderLocators,
        visitorLastReadTimestamp: visitorLastReadTimestamp,
        lastMessageId: lastMessageId,
        vid: vid,
        recipientVisitors: recipientVisitors,
        ungatedForNextBestActions: ungatedForNextBestActions,
        ungatedForReplyToAllMessages: ungatedForReplyToAllMessages,
        threadHistory: threadHistory,
        threadId: threadId,
        legacyRecipients: legacyRecipients,
        channelName: channelName,
        slots: slots
      }, getMessageId(message));
    });
  }
  render() {
    const {
      className,
      themeConfig
    } = this.props;
    return /*#__PURE__*/_jsx(ReflowElement, {
      className: className,
      children: /*#__PURE__*/_jsx(ThemeConfigContext.Provider, {
        value: themeConfig,
        children: this.renderHistory()
      })
    });
  }
}
MessageHistory.displayName = 'MessageHistory';
MessageHistory.propTypes = {
  allowContactPromotions: PropTypes.bool,
  channelName: PropTypes.string,
  className: PropTypes.string,
  fileAttachments: PropTypes.instanceOf(ImmutableMap),
  friendlyNames: PropTypes.array,
  getAdditionalPositionDescriptors: PropTypes.func,
  historyConfig: PropTypes.object.isRequired,
  indexedAsyncFileAttachments: AsyncFileAttachmentsMap,
  lastMessageId: PropTypes.string,
  legacyRecipients: LegacyRecipientMapPropType,
  messages: PropTypes.instanceOf(List),
  portalId: PropTypes.number,
  presentSenderLocators: PropTypes.instanceOf(ImmutableSet),
  recipientVisitors: PropTypes.instanceOf(ImmutableMap),
  senders: SenderMap,
  slots: PropTypes.object,
  themeConfig: PropTypes.object,
  threadHistory: RecordPropType('ThreadHistory'),
  threadId: PropTypes.string,
  ungatedForNextBestActions: PropTypes.bool,
  ungatedForReplyToAllMessages: PropTypes.bool,
  vid: PropTypes.number,
  visitorLastReadTimestamp: PropTypes.number
};
MessageHistory.defaultProps = {
  getAdditionalPositionDescriptors: () => ImmutableMap(),
  fileAttachments: ImmutableMap(),
  preventAutoscroll: false,
  presentSenderLocators: ImmutableSet(),
  scrollContainerToBottom: () => {},
  displayEmailDeleteBanner: false,
  ungatedForNextBestActions: false,
  ungatedForReplyToAllMessages: false
};
export default MessageHistory;